import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.round((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "1 day ago";
    if (days < 30) return `${days} days ago`;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

const VERDICT_PILL = {
  good: { bg: "#D5EBD9", fg: "#1F5030", label: "Good" },
  moderate: { bg: "#FBE9C4", fg: "#7A5A11", label: "Moderate" },
  avoid: { bg: "#F6C7BE", fg: "#7A2317", label: "Avoid" },
};

export default function OwnerProductLibrary() {
  const [tab, setTab] = useState("products");
  return (
    <div className="space-y-6" data-testid="owner-product-library">
      <header>
        <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">Shared library</div>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>Library</h1>
        <p className="text-sm text-[#5C6B61] mt-2 max-w-2xl">
          Products come from the Food Scanner (barcodes & photo scans). Recipes come from QR / URL / photo imports.
          Both pools are shared across every user, so the more people use the app the faster and smarter it gets.
        </p>
      </header>
      <div className="flex gap-1 rounded-full bg-[#F3F0EA] p-1 w-fit">
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="admin-tab-products">📦 Products</TabBtn>
        <TabBtn active={tab === "recipes"} onClick={() => setTab("recipes")} testid="admin-tab-recipes">🍲 Recipes</TabBtn>
      </div>
      {tab === "products" ? <ProductsPanel /> : <RecipesPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${active ? "bg-white text-[#2A332C] shadow-sm" : "text-[#5C6B61] hover:text-[#2A332C]"}`}
    >
      {children}
    </button>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5E2DA] bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}

function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalReuses, setTotalReuses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/product-library", { params: { q: query || undefined, limit: 500 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalReuses(data.total_reuses || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the product library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (key) => {
    if (!window.confirm("Delete this product? Next scan will re-run the AI.")) return;
    setBusy(key);
    try {
      await api.delete(`/admin/product-library/${encodeURIComponent(key)}`);
      setItems((xs) => xs.filter((x) => x.cache_key !== key));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } finally { setBusy(null); }
  };

  const refresh = async (key) => {
    setBusy(key);
    try {
      await api.post(`/admin/product-library/${encodeURIComponent(key)}/refresh`);
      setItems((xs) => xs.map((x) => (x.cache_key === key ? { ...x, updated_at: "1970-01-01T00:00:00+00:00" } : x)));
      toast.success("Marked stale");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark stale"); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Products cached" value={total} testid="lib-stat-total" />
        <StatCard label="Instant hits saved" value={totalReuses} testid="lib-stat-reuses" />
        <StatCard label="Showing" value={items.length} testid="lib-stat-showing" />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 items-center" data-testid="lib-search-form">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product name, brand or barcode" className="ff-input flex-1 text-sm" data-testid="lib-search-input" />
        <button type="submit" className="rounded-full bg-[#2A332C] text-white text-sm px-4 py-2" data-testid="lib-search-btn">Search</button>
        {q && <button type="button" onClick={() => { setQ(""); load(""); }} className="text-xs text-[#5C6B61] underline">Clear</button>}
      </form>
      <div className="rounded-2xl border border-[#E5E2DA] bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#8B9690]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#8B9690]">{total === 0 ? "No products cached yet — the library fills itself as users scan barcodes or product photos." : "No matches."}</div>
        ) : (
          <ul className="divide-y divide-[#E5E2DA]">
            {items.map((it) => {
              const pill = VERDICT_PILL[it.verdict] || VERDICT_PILL.moderate;
              const stale = !it.updated_at || new Date(it.updated_at).getTime() < Date.now() - 30 * 86400000;
              return (
                <li key={it.cache_key} className="p-4 flex items-center gap-3 flex-wrap md:flex-nowrap">
                  {it.image_url ? <img src={it.image_url} alt="" className="w-12 h-12 rounded-xl object-cover border border-[#E5E2DA] shrink-0" /> : <div className="w-12 h-12 rounded-xl bg-[#F3F0EA] flex items-center justify-center text-xl shrink-0">🥫</div>}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{it.product_name || "Unknown"}</div>
                    <div className="text-[11px] text-[#8B9690] truncate">{[it.brand, it.barcode].filter(Boolean).join(" · ") || "No brand"}</div>
                  </div>
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: pill.bg, color: pill.fg }}>{pill.label}</span>
                  <div className="shrink-0 text-[11px] text-[#5C6B61] text-right min-w-[110px]">
                    <div>Reused <b>{it.scan_count || 0}×</b></div>
                    <div className={stale ? "text-[#A33D26]" : ""}>Updated {fmtDate(it.updated_at)}{stale ? " · stale" : ""}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => refresh(it.cache_key)} disabled={busy === it.cache_key} className="text-xs px-2 py-1 rounded-full border border-[#E5E2DA] hover:border-[#3B5A3E] text-[#3B5A3E] disabled:opacity-50">Refresh</button>
                    <button onClick={() => remove(it.cache_key)} disabled={busy === it.cache_key} className="text-xs px-2 py-1 rounded-full border border-[#F6C7BE] hover:bg-[#FBE5DE] text-[#7A2317] disabled:opacity-50">Delete</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function RecipesPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/recipe-library", { params: { q: query || undefined, limit: 500 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the recipe library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (cid) => {
    if (!window.confirm("Delete this recipe from the shared pool?")) return;
    setBusy(cid);
    try {
      await api.delete(`/admin/recipe-library/${encodeURIComponent(cid)}`);
      setItems((xs) => xs.filter((x) => x.community_id !== cid));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Recipes in pool" value={total} testid="recipe-lib-total" />
        <StatCard label="Showing" value={items.length} testid="recipe-lib-showing" />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 items-center" data-testid="recipe-lib-search-form">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dish name or tag" className="ff-input flex-1 text-sm" data-testid="recipe-lib-search-input" />
        <button type="submit" className="rounded-full bg-[#2A332C] text-white text-sm px-4 py-2">Search</button>
        {q && <button type="button" onClick={() => { setQ(""); load(""); }} className="text-xs text-[#5C6B61] underline">Clear</button>}
      </form>
      <div className="rounded-2xl border border-[#E5E2DA] bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#8B9690]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#8B9690]">{total === 0 ? "No recipes shared yet — the pool fills itself as users import from QR, URL, or photo." : "No matches."}</div>
        ) : (
          <ul className="divide-y divide-[#E5E2DA]">
            {items.map((it) => (
              <li key={it.community_id} className="p-4 flex items-center gap-3 flex-wrap md:flex-nowrap">
                {it.image ? <img src={it.image} alt="" className="w-12 h-12 rounded-xl object-cover border border-[#E5E2DA] shrink-0" /> : <div className="w-12 h-12 rounded-xl bg-[#F3F0EA] flex items-center justify-center text-xl shrink-0">🍲</div>}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{it.title}</div>
                  <div className="text-[11px] text-[#8B9690] truncate">{(it.tags || []).slice(0, 4).join(" · ") || "—"}</div>
                </div>
                <div className="shrink-0 text-[11px] text-[#5C6B61] text-right min-w-[110px]">
                  <div>Saved <b>{it.scan_count || 1}×</b></div>
                  {typeof it.calories_per_serving === "number" && <div>{Math.round(it.calories_per_serving)} kcal</div>}
                </div>
                <button onClick={() => remove(it.community_id)} disabled={busy === it.community_id} className="text-xs px-2 py-1 rounded-full border border-[#F6C7BE] hover:bg-[#FBE5DE] text-[#7A2317] disabled:opacity-50">Delete</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
